import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { Params } from '@angular/router';
import { RouterParamsService } from '../../../../shared/services/router-params.service';
import { AclService } from '../../../../shared/services/acl.service';
import { AccessControlList } from '../../../models/access-control-list.model';

@Injectable()
export class BaseEntityService<T> {
  aclService: AclService = inject(AclService);
  private entitiesSubject = new BehaviorSubject<T[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private entitiesMap = new Map<string, T>();
  private _paginationSubject = new Subject<T[]>();

  public singularEntityName: string;
  public pluralEntityName: string;
  public httpClient: HttpClient;
  public nameId: string;
  private routerParamsService: RouterParamsService;
  private readonly permissions?: AccessControlList;
  public entityUrl: any;
  private encryptedEntity: boolean = false;

  constructor(
    private http: HttpClient,
    baseUrl: string,
    entityNames: { singular: string; plural: string, url?: string },
    routerParamsService: RouterParamsService,
    permissions?: AccessControlList,
    encryptedEntity: boolean = false,
  ) {
    this.encryptedEntity = encryptedEntity;
    this.permissions = permissions;
    const { singular, plural, url } = entityNames;
    this.singularEntityName = singular;
    this.pluralEntityName = plural;
    this.entityUrl = [baseUrl, url ?? plural].join('/');
    this.httpClient = http;
    this.routerParamsService = routerParamsService;
  }

  get hasPermissions(): boolean {
    return !!this.permissions;
  }

  public validatePermission(action: string, breakValidation: boolean = false): boolean {
    if (this.hasPermissions) {
      if (!this.aclService.hasPermission(this.permissions[action])) {
        this._paginationSubject.next(this.getEntities);
        const errorMsg = `User has no permission to ${ action } in ${ this.singularEntityName } entity`;
        console.error(errorMsg);
        if (breakValidation) {
          throw Error(errorMsg);
        }
        return false;
      }
      return true;
    }
    // tslint:disable-next-line:no-console
    // console.info(this.singularEntityName, `front allowed ${ action }`);
    return true;
  }

  get paginationSubject(): Subject<T[]> {
    return this._paginationSubject;
  }

  set paginationSubject(value: T[]) {
    this._paginationSubject.next(value);
  }

  get getEntities$(): Observable<T[]> {
    return this.entitiesSubject.asObservable();
  }

  get getEntities() {
    return [...this.entitiesMap.values()];
  }

  clearAll(): void {
    this.loadingSubject.next(false);
    this.entitiesSubject.next([]);
    this.entitiesMap.clear();
  }

  set manualEntity(newEntity: T) {
    this.entitiesMap.set((newEntity as any).id, newEntity);
    this.updateEntity();
  }

  public getCurrent(): Observable<T> {
    return this.routerParamsService.params.pipe(
      filter(params => !!params[this.nameId]),
      map(params => params[this.nameId]),
      switchMap(id => this.getEntityById(id)),
    );
  }

  public getCurrentFromApi(firstAvailable = false): Observable<T> {
    return this.routerParamsService.params.pipe(
      filter(params => !!params[this.nameId]),
      map(params => parseInt(params[this.nameId], 10)),
      switchMap(ids => {
          if (firstAvailable) {
            const tFound = this.getEntities.find(t => (t as any).id === ids);
            if (tFound) {
              return of(tFound);
            }
          }
          return this.getWithQuery({ ids }).pipe(
            map(response => {
              const [entity] = response;
              return entity;
            }),
          );
        },
      ),
    );
  }

  getWithQuery(queryParams: Params, disableDefaultWrap: boolean = false): Observable<T[]> {
    if (!this.validatePermission('read')) {
      return of([]);
    }
    this.loadingSubject.next(true);
    const params: Params = this.removeUndefinedOrNull(queryParams);

    let responseType: XMLHttpRequestResponseType = 'json';
    if (this.encryptedEntity) {
      responseType = 'any' as 'json';
    }

    return this.http.get<T[]>(this.entityUrl, { params, responseType }).pipe(
      map(response => disableDefaultWrap ? response ?? [] : response[this.pluralEntityName] ?? []),
      tap(entities => {
        if (!disableDefaultWrap) {
          entities.forEach((entity: T) => this.entitiesMap.set((entity as any).id, entity));
          this.updateEntity();
          this._paginationSubject.next(this.getEntities);
        }
      }),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  add(entity: T, disableDefaultWrap: boolean = false): Observable<T> {
    this.validatePermission('create', true);

    const body = disableDefaultWrap ? entity : { [this.singularEntityName]: entity };
    this.loadingSubject.next(true);

    let responseType: XMLHttpRequestResponseType = 'json';
    if (this.encryptedEntity) {
      responseType = 'arraybuffer' as 'json';
    }

    return this.http.post<T>(this.entityUrl, body, { responseType }).pipe(
      map(response => disableDefaultWrap ? response ?? {} : response[this.singularEntityName] ?? {}),
      tap(newEntity => {
        if (!disableDefaultWrap) {
          this.entitiesMap.set((newEntity as any).id, newEntity);
          this.updateEntity();
        }
      }),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  update(entity: T, disableDefaultWrap: boolean = false): Observable<T> {
    this.validatePermission('update', true);

    const body = disableDefaultWrap ? { ...entity } : { [this.singularEntityName]: { ...entity } };
    delete body[this.singularEntityName]['id'];
    this.loadingSubject.next(true);
    const { id } = entity as any;

    let responseType: XMLHttpRequestResponseType = 'json';
    if (this.encryptedEntity) {
      responseType = 'arraybuffer' as 'json';
    }

    return this.http.patch<T>([this.entityUrl, id].join('/'), body, { responseType }).pipe(
      map(response => {
        if (disableDefaultWrap) {
          return response;
        }
        if (!!response?.hasOwnProperty(this.singularEntityName)) {
          return response[this.singularEntityName];
        }
        return response;
      }),
      tap(updatedEntity => {
        if (!disableDefaultWrap && updatedEntity?.hasOwnProperty(this.singularEntityName)) {
          this.entitiesMap.set(id, updatedEntity);
          this.updateEntity();
        }
      }),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  delete(id: number): Observable<void> {
    this.validatePermission('remove', true);

    this.loadingSubject.next(true);
    return this.http.delete<void>([this.entityUrl, id].join('/')).pipe(
      tap(() => {
        try {
          this.entitiesMap.delete(id.toString());
          this.updateEntity();
        } catch (e) {
          console.error(e);
        }
      }),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  saveEntity(enity: T): Observable<T> {
    if ((enity as any).id) {
      return this.update(enity);
    }
    return this.add(enity);
  }

  getById(ids: any): Observable<T> {
    this.validatePermission('read', true);

    this.loadingSubject.next(true);

    let responseType: XMLHttpRequestResponseType = 'json';
    if (this.encryptedEntity) {
      responseType = 'arraybuffer' as 'json';
    }

    return this.http.get<T>([this.entityUrl].join('/'), {
      params: {
        ids,
      },
      responseType
    }).pipe(
      map(response => response[this.pluralEntityName] ?? []),
      map(response => {
        const [entity] = response;
        return entity;
      }),
      tap((updatedEntity) => {
        this.entitiesMap.set(ids, updatedEntity);
        this.updateEntity();
      }),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  getByIds(id: any): Observable<T> {
    return this.getById(id);
  }

  getEntityById(id: any): Observable<T> {
    id = parseInt(id, 10);
    this.loadingSubject.next(true);
    return this.getEntities$.pipe(
      filter(entities => entities.length > 0 && entities.some(e => (e as any).id === id)),
      map(entities => entities.find(e => (e as any).id === id)),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  removeUndefinedOrNull(obj: { [key: string]: any }): { [key: string]: any } {
    const cleanedObj: { [key: string]: any } = {};
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (value !== undefined && value !== null) {
        cleanedObj[key] = value;
      }
    });
    return cleanedObj;
  }

  updateEntity(): void {
    this.entitiesMap = this.removeDuplicatesByObjectIdFromMap(this.entitiesMap);
    this.entitiesSubject.next(this.getEntities);
  }

  removeDuplicatesByObjectIdFromMap<K, T>(map: Map<K, T>): Map<K, T> {
    const seenIds = new Set<any>();
    const result = new Map<K, T>();
    for (const [key, value] of map) {
      if (!seenIds.has((value as any).id)) {
        seenIds.add((value as any).id);
        result.set(key, value);
      }
    }
    return result;
  }
}

